import React from "react";
import { ScrollContext } from "../contexts/scroll_context.js.jsx";

/**
 * Use for listening scrolling
 * 
 * Useful only inside component, wrapped into withScrollHandler
 * 
 * Wrapped component must have refs scrollContent and scrollContainer
 * @component
 * @category HOC
 * @param providedProps
 * @param {boolean} providedProps.scrolledToEndProcessing - True, if scrollable content reached end
 * @param {boolean} providedProps.shouldProcessScrollToEnd - Allow/disallow processing scroll to end
 * @param {func(shouldProcessScrollToEnd)} providedProps.onScrollToEndProcessed - Call it, for disable listening scrolling for infinite scrolling
 * @param {func(hasMore)} providedProps.onDataLoaded - Call it, when next page loading will end
 * @param {func(element)} providedProps.scrollToElement - Call it, for scroll to html element inside scroll content
*/
const WithScrollListener = (Component) => {

  class WithScrollListener extends React.Component {

    constructor(props) {
      super(props);

      this.state = {

      }

      this.child = React.createRef();
    }

    static getDerivedStateFromProps(props, state) {
      const { context } = props;
      return {
        scrolledToEndProcessing: context.scrolledToEndProcessing,
        shouldProcessScrollToEnd: context.shouldProcessScrollToEnd,
      }
    }

    componentDidUpdate(prevProps, prevState) {
      const { scrolledToEndProcessing: prevScrolledToEndProcessing } = prevState;
      const { scrolledToEndProcessing, shouldProcessScrollToEnd } = this.state;
      if(!shouldProcessScrollToEnd) {
        return;
      }

      if(scrolledToEndProcessing && scrolledToEndProcessing !== prevScrolledToEndProcessing) {
        this.onScrolledToEnd();
      }
    }

    onScrolledToEnd = () => {
      if(!this.child || !this.child.current) {
        return;
      }
      const { current: { onScrolledToEnd } = {} } = this.child;
      if(onScrolledToEnd) {
        onScrolledToEnd();
      }
    }

    render() {
      return (
        <Component { ...this.props } 
                   ref = { this.child }/>
      )
    }
  }

  WithScrollListener.contextType = ScrollContext;

  const consumer = (props) => {
    return (
      <ScrollContext.Consumer>
        { context => 
          <WithScrollListener { ...props } 
                              context = { context }
                              onScrollToEndProcessed = { context.onScrollToEndProcessed }
                              onDataLoaded = { context.onDataLoaded }
                              scrollToElement = { context.scrollToElement } />
        }
      </ScrollContext.Consumer>
    )
  }

  return consumer;
};

export default WithScrollListener;